import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {infiniteQueryOptions, QueryClient, useInfiniteQuery, useQueryClient} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {parseLinkHeader} from '../../utils/parse-link-header'
import type {
  ConversationComment,
  ConversationCommentPayload,
  ConversationCommentsPagePayload,
  ConversationCommentType,
  ConversationIssueComment,
  ConversationReviewSummary,
} from '../payloads/conversation-comments'
import {produce, type WritableDraft} from 'immer'
import {throwErrorsIfBadResponse} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useCallback, useId, useSyncExternalStore} from 'react'
import {noop} from '@github-ui/noop'
import {useAlive} from '@github-ui/use-alive'
import {threadsQueryKey} from './use-threads'
import {useInvalidateOnAliveUpdate, type AliveUpdateData} from '@github-ui/use-invalidate-on-alive-update'
import {useAsyncQueuer} from '@tanstack/react-pacer'

export interface ConversationCommentsPage {
  data: ConversationCommentsPagePayload['items']
  nextUrl: string | null
  aliveChannel?: string
}

export function conversationCommentsQueryKey(basePath: string) {
  return conversationCommentsQueryOptions(basePath).queryKey
}

interface UseInfiniteConversationCommentsQueryOpts {
  basePath: string
  enabled?: boolean
}

/** Use paged comments query. **Automatically subscribes to live updates.** */
export function useInfiniteConversationCommentsQuery({
  basePath,
  enabled = true,
}: UseInfiniteConversationCommentsQueryOpts) {
  const query = useInfiniteQuery({
    ...conversationCommentsQueryOptions(basePath),
    enabled,
  })
  useConversationCommentsLiveUpdates({enabled, basePath, channel: query.data?.aliveChannel})
  return query
}

export function conversationCommentsApiUrl(basePath: string): string {
  return `${basePath}/page_data/${PageData.conversationComments}`
}

/** Fetch a page of comment items. */
async function fetchConversationCommentsPage(url: string): Promise<ConversationCommentsPage> {
  const parsedUrl = new URL(url, window.location.origin)
  parsedUrl.searchParams.set('direction', 'desc') // always fetch newest comments first, preserving other params

  const response = await reactFetchJSON(parsedUrl.toString())
  throwErrorsIfBadResponse(response)

  const json = (await response.json()) as ConversationCommentsPagePayload
  const linkHeader = response.headers.get('Link')
  const nextUrl = parseLinkHeader(linkHeader, 'next')

  return {data: json.items, aliveChannel: json.aliveChannel ?? undefined, nextUrl}
}

export function singleConversationCommentApiUrl(basePath: string, type: ConversationCommentType, id: number): string {
  return `${conversationCommentsApiUrl(basePath)}/${type}/${id}`
}

/** Fetch a single comment item. */
export async function fetchSingleConversationComment(basePath: string, type: ConversationCommentType, id: number) {
  const url = singleConversationCommentApiUrl(basePath, type, id)
  const response = await reactFetchJSON(url)
  throwErrorsIfBadResponse(response)

  return (await response.json()) as ConversationCommentPayload
}

function conversationCommentsQueryOptions(basePath: string) {
  return infiniteQueryOptions({
    queryKey: [PageData.conversationComments, basePath],
    initialPageParam: conversationCommentsApiUrl(basePath),
    queryFn: ({pageParam}) => fetchConversationCommentsPage(pageParam),
    getNextPageParam: lastPage => lastPage.nextUrl,
    select: ({pages}) => ({
      comments: pages.flatMap(page => page.data),
      aliveChannel: pages.find(p => p.aliveChannel !== undefined)?.aliveChannel ?? undefined,
    }),
    // CAUTION: If we ever add a nonzero staleTime here (which we probably should to preserve data when switching modes)
    // we will need to change the logic for live updates unmounts (the mountedUseConversationLiveUpdatesHooksStore) to
    // ensure that the updates remain active until the data becomes stale.
  })
}

/**
 * Helper to update a single issue comment in the conversation comments query store. Can be used for partial updates
 * without overwriting the entire item.
 * @param updateComment A function to update the comment. The passed item is a **mutable** (`immer`) draft;
 * you should edit this and return void.
 */
export function setCommentQueryData(
  client: QueryClient,
  basePath: string,
  commentId: number,
  updateComment: (draft: WritableDraft<ConversationIssueComment>) => void,
) {
  client.setQueryData(
    conversationCommentsQueryKey(basePath),
    data =>
      data &&
      produce(data, draftData => {
        for (const page of draftData.pages)
          for (const comment of page.data)
            // IDs are not unique across types, so it's important to also check `type`
            if (comment.type === 'issue' && comment.id === commentId) {
              updateComment(comment)
              return
            }
      }),
  )
}

/**
 * Remove a single comment from the store.
 * Note that there is no corresponding helper for review summaries as they cannot be deleted.
 */
export function removeCommentQueryData(client: QueryClient, basePath: string, commentId: number) {
  client.setQueryData(
    conversationCommentsQueryKey(basePath),
    data =>
      data &&
      produce(data, draftData => {
        for (const page of draftData.pages)
          page.data = page.data.filter(comment => comment.type !== 'issue' || comment.id !== commentId)
      }),
  )
}

/**
 * Update a single review summary in the conversation comments query store. Can be used for partial updates without
 * overwriting the entire item.
 * @param updateComment A function to update the review summary. The passed item is a **mutable** (`immer`) draft;
 * you should edit this and return void.
 */
export function setReviewSummaryQueryData(
  client: QueryClient,
  basePath: string,
  commentId: number,
  updateComment: (draft: WritableDraft<ConversationReviewSummary>) => void,
) {
  client.setQueryData(
    conversationCommentsQueryKey(basePath),
    data =>
      data &&
      produce(data, draftData => {
        for (const page of draftData.pages)
          for (const comment of page.data)
            // IDs are not unique across types, so it's important to also check `type`
            if (comment.type === 'review_summary' && comment.id === commentId) {
              updateComment(comment)
              return
            }
      }),
  )
}

/**
 * Fully replace any item (by type and ID) in the query data. Has no effect if the item does not exist yet.
 * @returns `true` if the item was found and updated.
 */
export function replaceConversationItemQueryData(client: QueryClient, basePath: string, comment: ConversationComment) {
  let didUpdate = false

  client.setQueryData(
    conversationCommentsQueryKey(basePath),
    data =>
      data &&
      produce(data, draftData => {
        for (const page of draftData.pages)
          for (const existingComment of page.data)
            if (existingComment.type === comment.type && existingComment.id === comment.id) {
              Object.assign(existingComment, comment)
              didUpdate = true
              return
            }
      }),
  )

  return didUpdate
}

/**
 * Update or add a new item to the query data, while ensuring that we never create duplicates. The item is assumed to
 * be the most recently created comment and is thus always pushed to the beginning of the data.
 */
export function upsertConversationItemQueryData(client: QueryClient, basePath: string, comment: ConversationComment) {
  if (replaceConversationItemQueryData(client, basePath, comment)) return

  client.setQueryData(
    conversationCommentsQueryKey(basePath),
    data =>
      data &&
      produce(data, draftData => {
        // Since pages are always loaded in recent-first order, new items should always be newer than any existing pages,
        // so we just push the new item to the beginning. Also, pushing a new page to the end would override the last
        // page's nextUrl and break subsequent page loading. It's a bit unusual to have a page of one item but we flatten
        // the pages anyway so it should work fine.
        draftData.pages.unshift({data: [comment], nextUrl: null})
      }),
  )
}

function conversationItemExistsInQueryData(
  client: QueryClient,
  basePath: string,
  type: ConversationCommentType,
  id: number,
) {
  return client
    .getQueryData(conversationCommentsQueryKey(basePath))
    ?.pages.some(page => page.data.some(comment => comment.type === type && comment.id === id))
}

interface ConversationCommentLiveUpdateData extends AliveUpdateData {
  /** Updated item ID. */
  id: number
  item_type: ConversationCommentType
  event_type: 'create' | 'update' | 'delete'
}

/** Tracks which hook is the first currently mounted one. */
const mountedUseConversationLiveUpdatesHooksStore = (() => {
  const mountedHooks = new Map<string, () => void>()

  const notifyObservers = () => {
    for (const notify of mountedHooks.values()) notify()
  }

  const unsubscribe = (id: string) => {
    mountedHooks.delete(id)
    notifyObservers()
  }

  const subscribe = (id: string, notify: () => void) => {
    mountedHooks.set(id, notify)
    notifyObservers()

    return () => unsubscribe(id)
  }

  const getSnapshot = (id: string) => Array.from(mountedHooks.keys())[0] === id

  return {subscribe, getSnapshot}
})()

/** Incrementally refreshes loaded conversation comments after timeline updates. */
function useConversationCommentsLiveUpdates({
  basePath,
  enabled,
  channel,
}: UseInfiniteConversationCommentsQueryOpts & {
  channel: string | undefined
}): void {
  const id = useId()

  // To avoid duplicated requests, only the first mounted (and enabled) instance of useConversationCommentsLiveUpdates
  // will be subscribed to the channel. If the first instance unmounts, the second instance will take over the subscription.
  // `useInvalidateOnAliveUpdate` would handle this kind of thing automatically, but we can't use it here because we are
  // performing targeted store updates instead of full invalidations.
  const isFirstMountedHook = useSyncExternalStore(
    useCallback(
      notify => {
        if (!enabled) return noop
        return mountedUseConversationLiveUpdatesHooksStore.subscribe(id, notify)
      },
      [id, enabled],
    ),
    () => mountedUseConversationLiveUpdatesHooksStore.getSnapshot(id),
    () => false,
  )

  const liveUpdatesEnabled = isFirstMountedHook && isFeatureEnabled('prs_new_conversation_comments_api_live_updates')
  const enabledChannel = liveUpdatesEnabled ? channel : undefined

  const client = useQueryClient()

  const handleUpdate = useCallback(
    async (data: ConversationCommentLiveUpdateData) => {
      if (data.event_type === 'delete') {
        // Deletion is only supported for comments
        if (data.item_type !== 'issue') return

        removeCommentQueryData(client, basePath, data.id)
        return
      }

      const isKnownItem = conversationItemExistsInQueryData(client, basePath, data.item_type, data.id)

      if (data.event_type === 'create') {
        // If we already know about this item, there's no need to waste resources fetching it
        if (isKnownItem) return

        const comment = await fetchSingleConversationComment(basePath, data.item_type, data.id)
        upsertConversationItemQueryData(client, basePath, comment)
        return
      }

      if (data.event_type === 'update') {
        // If we don't know about this item, it's probably on a page we haven't loaded yet so we can ignore the update
        if (!isKnownItem) return

        const comment = await fetchSingleConversationComment(basePath, data.item_type, data.id)
        replaceConversationItemQueryData(client, basePath, comment)
        return
      }
    },
    [client, basePath],
  )

  // We don't want to handle events as fast as they come in, because handling is async and could resolve out of order
  // (a fast delete, for example, might be handled before the create event finishes). But we also can't just throttle
  // and drop events like we do elsewhere, because we aren't invalidating the entire query - we want to handle every event.
  const updateQueue = useAsyncQueuer(handleUpdate, {concurrency: 1, onUnmount: q => q.flush()})

  useAlive<ConversationCommentLiveUpdateData>(enabledChannel, data => updateQueue.addItem(data))

  // When a new review summary is added, invalidate threads to ensure we fetch the new threads. Since this is a pure
  // invalidation (for now), we can use `useInvalidateOnAliveUpdate` and take advantage of global dedupe for that
  // query key.
  // TODO - follow up with targeted fetching for the review summary's specific threads
  useInvalidateOnAliveUpdate<ConversationCommentLiveUpdateData>(enabledChannel, threadsQueryKey(basePath), {
    filter: data => data.event_type === 'create' && data.item_type === 'review_summary',
  })
}
